import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { RotateCcw, ArrowLeft, LifeBuoy } from "lucide-react";
import { Wordmark } from "./Wordmark";

// App-level crash net. A render error anywhere below this boundary shows a
// calm, on-brand recovery screen instead of a blank page. The boundary
// resets itself when the route changes, so "Back to your case plan" (or
// the browser back button) recovers without a full reload.

type Props = { children: ReactNode; resetKey?: string };
type State = { error: Error | null };

class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the details in the console for us; the user sees plain words.
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <CrashScreen message={this.state.error.message} />;
  }
}

function CrashScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-soft text-ink flex flex-col">
      <header className="h-14 flex items-center px-4 sm:px-8 border-b border-hair bg-surface">
        <Wordmark size={18} />
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="card w-full max-w-[520px] p-6 sm:p-8">
          <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center bg-terra-50 text-terra-700">
            <LifeBuoy className="w-6 h-6" aria-hidden />
          </span>
          <h1 className="font-display text-h2 text-ink mt-5">This screen hit a snag.</h1>
          <p className="text-body text-ink-2 mt-2">
            Something went wrong while showing this page. Your case is safe —
            everything you've saved is still on file. Reloading usually fixes it.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              className="btn-primary h-11 px-5 text-body whitespace-nowrap"
              onClick={() => window.location.reload()}
            >
              <RotateCcw className="w-4 h-4" aria-hidden /> Reload this page
            </button>
            <a
              href="/scholar-path"
              className="btn-secondary h-11 px-5 text-body whitespace-nowrap"
              style={{ textDecoration: "none" }}
            >
              <ArrowLeft className="w-4 h-4" aria-hidden /> Back to your case plan
            </a>
          </div>
          {message && (
            <details className="mt-6 text-small text-ink-3">
              <summary className="cursor-pointer select-none">Technical details</summary>
              <code className="mt-2 block font-mono text-caption break-words text-ink-2">{message}</code>
            </details>
          )}
        </div>
      </main>
    </div>
  );
}

/** Route-aware error boundary: clears the error when the path changes. */
export function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <Boundary resetKey={location.pathname}>{children}</Boundary>;
}

export default ErrorBoundary;
